/*
  Loads per-section component bundles. Sections far below the viewport wait
  for an IntersectionObserver (2 viewport heights ahead); eager paths:
  data-load="eager", no IO support, hidden targets (drawers/modals - IO
  would never fire), or targets already near the viewport.
*/
(function () {
  // Sources already appended by any instance; prevents the same bundle being
  // appended twice when two sections share a component and both trigger
  // before the registry marks the script as loaded. Keyed by src, not by
  // load key: different bundles may share a key.
  const requestedSrcs = new Set();

  class SafeLoadScripts extends HTMLElement {
    connectedCallback() {
      if (this.dataset.load === 'eager' || !('IntersectionObserver' in window)) {
        this.loadScripts();
        return;
      }

      // The element itself is display:none (class="hidden"), so observe the
      // owning section (or parent), which has real geometry.
      const target = this.closest('.shopify-section') || this.parentElement || this;
      const rect = target.getBoundingClientRect();
      const hasBox = rect.width > 0 && rect.height > 0;
      const margin = Math.max(window.innerHeight * 2, 1500);

      if (!hasBox || rect.top < margin) {
        this.loadScripts();
        return;
      }

      this._observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this._observer.disconnect();
          this._observer = null;
          this.loadScripts();
        }
      }, { rootMargin: margin + 'px 0px' });
      this._observer.observe(target);
    }

    disconnectedCallback() {
      if (this._observer) {
        this._observer.disconnect();
        this._observer = null;
      }
    }

    loadScripts() {
      const scripts = this.querySelectorAll('safe-load-scripts > script');

      scripts.forEach((script) => {
        const key = script.dataset.flowLoadKey;

        const scriptLoaded = window.wetheme.webcomponentRegistry.checkScriptLoaded(key);
        if (!scriptLoaded && !requestedSrcs.has(script.src)) {
          requestedSrcs.add(script.src);

          const postLoadedScript = document.createElement('script');
          const { src, defer, async, type } = script;
          postLoadedScript.src = src;
          postLoadedScript.defer = defer;
          postLoadedScript.async = async;
          postLoadedScript.type = type;

          this.parentElement.appendChild(postLoadedScript);
        }
      });
    }
  }

  customElements.define('safe-load-scripts', SafeLoadScripts);
})();
